// react_frontend/src/Components/Modules/PurchaseSetting/StockOutList.js
import React, { useEffect, useState, useMemo } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link } from "react-router-dom";

// RSuite Components
import {
  Button,
  Input,
  Badge,
  Loader,
  Whisper,
  Tooltip,
  Message,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// External Libraries
import Swal from "sweetalert2";
import moment from "moment";

// Icons
import {
  FaHistory,
  FaCalendarAlt,
  FaTags,
  FaBox,
  FaArrowUp,
  FaArrowDown,
  FaUser,
  FaTrashAlt,
  FaSearch,
  FaSync,
} from "react-icons/fa";

const StockOutList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const isAdminOrAccount = ["1", "2"].includes(localStorage.getItem("who_am_i"));

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await AxiosAuthInstance.get("inventory/stockout");
      setData(response.data.transactions || []);
    } catch (error) {
      console.error("Failed to load stock out history:", error);
      toaster.push(
        <Message type="error" duration={5000}>
          Failed to load stock out data.
        </Message>,
        { placement: "topEnd" }
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      (item.category?.category_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (item.item?.item_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (item.stock_out_date || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search]);

  const handleDelete = (record) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.delete(`/inventory/stockout/${record.id}`);
          setData((prev) => prev.filter((r) => r.id !== record.id));
          toaster.push(
            <Message type="success" duration={3000}>
              Record deleted successfully!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          toaster.push(
            <Message type="error" duration={5000}>
              Failed to delete record.
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };

  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "100px",
      center: true,
    },
    {
      name: "Date",
      selector: (row) => row.stock_out_date,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaCalendarAlt className="text-primary" />
          <small className="text-dark fw-medium">
            {moment(row.stock_out_date).format("MMM DD, YYYY")}
          </small>
        </div>
      ),
    },
    {
      name: "Category",
      selector: (row) => row.category?.category_name,
      sortable: true,
      width: "250px",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaTags /> {row.category?.category_name || "N/A"}
        </Badge>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.item?.item_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          {row.stock_status === "return" ? (
            <FaArrowDown className="text-success" />
          ) : (
            <FaArrowUp className="text-danger" />
          )}
          <span className="fw-medium">{row.item?.item_name || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "Quantity",
      selector: (row) =>
        row.stock_status === "return" ? row.return_goods : row.used_quantity,
      sortable: true,
      width: "120px",
      cell: (row) => {
        const qty =
          row.stock_status === "return" ? row.return_goods : row.used_quantity;
        return (
          <Badge
            color={row.stock_status === "return" ? "green" : "red"}
            content={`${qty} ${row.product_unit}`}
            style={{ fontSize: "0.85em" }}
          />
        );
      },
    },
    {
      name: "Type",
      width: "120px",
      cell: (row) => (
        <Badge
          color={row.stock_status === "return" ? "green" : "red"}
          content={row.stock_status === "return" ? "Return" : "Stock Out"}
          style={{ fontSize: "0.8em" }}
        />
      ),
      center: true,
    },
    // {
    //   name: "Created By",
    //   sortable: true,
    //   width: "160px",
    //   cell: (row) => {
    //     const creatorName = row.creator?.name || row.creator_name || "Unknown";
    //     return (
    //       <div className="d-flex align-items-center gap-2">
    //         <FaUser className="text-primary" />
    //         <div className="fw-medium" style={{ whiteSpace: "nowrap" }}>
    //           {creatorName}
    //         </div>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   name: "Updated By",
    //   sortable: true,
    //   width: "160px",
    //   cell: (row) => {
    //     const updatorName = row.updator?.name || row.updator_name || "—";
    //     return (
    //       <div className="d-flex align-items-center gap-2">
    //         <FaUser className="text-secondary" />
    //         <div
    //           className={updatorName === "—" ? "text-muted" : "text-dark"}
    //           style={{ whiteSpace: "nowrap" }}
    //         >
    //           {updatorName}
    //         </div>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   name: "Created At",
    //   selector: (row) => row.created_at,
    //   sortable: true,
    //   width: "180px",
    //   cell: (row) => (
    //     <div className="d-flex align-items-center gap-1">
    //       <FaCalendarAlt className="text-success" />
    //       <small>
    //         {moment(row.created_at).format("MMM DD, h:mm A")}
    //       </small>
    //     </div>
    //   ),
    // },
    // {
    //   name: "Updated At",
    //   selector: (row) => row.updated_at,
    //   sortable: true,
    //   width: "180px",
    //   cell: (row) => (
    //     <div className="d-flex align-items-center gap-1">
    //       <FaCalendarAlt className="text-warning" />
    //       <small>
    //         {row.updated_at === row.created_at ? (
    //           <span className="text-muted">—</span>
    //         ) : (
    //           moment(row.updated_at).format("MMM DD, h:mm A")
    //         )}
    //       </small>
    //     </div>
    //   ),
    // },
    {
      name: "Actions",
      cell: (row) =>
        isAdminOrAccount ? (
          <Whisper
            placement="top"
            speaker={<Tooltip>Delete this record</Tooltip>}
          >
            <Button
              size="sm"
              color="red"
              startIcon={<FaTrashAlt />}
              onClick={() => handleDelete(row)}
            >
              Delete
            </Button>
          </Whisper>
        ) : (
          <Badge color="default">No Access</Badge>
        ),
      width: "140px",
      center: true,
    },
  ];

  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "70px",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
      },
    },
  };

  const AddButtons = () => (
    <div className="d-flex gap-2">
      <Whisper placement="top" speaker={<Tooltip>Record stock usage</Tooltip>}>
        <Link to="/stock_out" style={{ textDecoration: "none" }}>
          <Button
            appearance="primary"
            startIcon={<FaArrowUp />}
            size="md"
            className="d-flex align-items-center"
          >
            Stock Out
          </Button>
        </Link>
      </Whisper>
      <Whisper placement="top" speaker={<Tooltip>View current stock</Tooltip>}>
        <Link to="/stock_list" style={{ textDecoration: "none" }}>
          <Button
            appearance="subtle"
            startIcon={<FaBox />}
            size="md"
            className="d-flex align-items-center"
          >
            Stock List
          </Button>
        </Link>
      </Whisper>
      {/* Manual Refresh Button */}
      <Button
        appearance="ghost"
        startIcon={<FaSync />}
        onClick={fetchData}
        size="md"
      >
        Refresh
      </Button>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Stock Out History" />
      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaHistory className="me-2" />
          Stock Usage & Returns
        </h5>
        <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
          <Input
            placeholder="Search by item, category, or date..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <AddButtons />
        </div>
      </div>
      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loading}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading records...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaHistory size={48} className="mb-3 text-secondary" />
            <h5>No records found</h5>
            <p>Try adjusting your search or record a new stock action.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default StockOutList;